import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { DayPicker, DateRange } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { FiCalendar, FiX } from 'react-icons/fi'
import { Button, Text, tokens } from 'src/componentv2'
import './style.scss'

export interface DateRangeFilterButtonProps {
    value?: DateRange
    onChange: (range?: DateRange) => void
}

function formatRangeLabel(range?: DateRange) {
    if (!range?.from) return 'Select date range'
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    if (range.to) return `${fmt(range.from)} - ${fmt(range.to)}`
    return fmt(range.from)
}

const POPOVER_WIDTH = 320
const POPOVER_GAP = 8

export const DateRangeFilterButton: React.FC<DateRangeFilterButtonProps> = ({ value, onChange }) => {
    const [open, setOpen] = useState(false)
    const [draft, setDraft] = useState<DateRange | undefined>(value)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

    const triggerRef = useRef<HTMLButtonElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)

    const computePosition = () => {
        const btn = triggerRef.current
        if (!btn) return

        const rect = btn.getBoundingClientRect()
        const spaceRight = window.innerWidth - rect.right
        const openToRight = spaceRight >= POPOVER_WIDTH + POPOVER_GAP

        setPosition({
            top: rect.bottom + POPOVER_GAP + window.scrollY,
            left: openToRight
                ? rect.left + window.scrollX
                : Math.max(8, rect.right + window.scrollX - POPOVER_WIDTH),
        })
    }

    useEffect(() => {
        if (!open) return
        computePosition()

        const reposition = () => computePosition()
        window.addEventListener('resize', reposition)
        window.addEventListener('scroll', reposition, true)
        return () => {
            window.removeEventListener('resize', reposition)
            window.removeEventListener('scroll', reposition, true)
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                popoverRef.current && !popoverRef.current.contains(target) &&
                triggerRef.current && !triggerRef.current.contains(target)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    const handleOpen = () => {
        setDraft(value)
        setOpen(true)
    }

    const handleApply = () => {
        onChange(draft)
        setOpen(false)
    }

    const handleClear = () => {
        setDraft(undefined)
        onChange(undefined)
        setOpen(false)
    }

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                className={`date-range-filter__trigger ${value?.from ? 'date-range-filter__trigger--active' : ''}`}
                onClick={handleOpen}
            >
                <FiCalendar size={16} className="date-range-filter__trigger-icon" />
                <span className="date-range-filter__trigger-label">{formatRangeLabel(value)}</span>
                {value?.from && (
                    <FiX
                        size={14}
                        className="date-range-filter__trigger-clear"
                        onClick={(e) => {
                            e.stopPropagation()
                            onChange(undefined)
                        }}
                    />
                )}
            </button>

            {open && position && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    className="date-range-filter__popover"
                    style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <DayPicker
                        mode="range"
                        selected={draft}
                        onSelect={setDraft}
                        disabled={{ after: new Date() }}
                        numberOfMonths={1}
                    />

                    <Text fontSize="1.3" color={tokens.textMuted} className="date-range-filter__preview">
                        {formatRangeLabel(draft)}
                    </Text>

                    <div className="date-range-filter__actions">
                        <Button variant="outline" onClick={handleClear}>
                            Clear
                        </Button>
                        <Button onClick={handleApply} disabled={!draft?.from}>
                            Apply
                        </Button>
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}

export default DateRangeFilterButton